import axios from "axios";
import { createStore } from "zustand/vanilla";
import { useEffect, useState } from "react";
import { getCookie } from "../../../../../utils/cookieUtils";
import { errorNoti } from "../../../../../base/BasicNotifications";
import {
  ACCESS_TOKEN_COOKIE_KEY,
  ADMIN_ACCESS_TOKEN_COOKIE_KEY,
  ADMINS_COACH_ID_COOKIE_KEY,
} from "../../../../../data/enums/TokenEnums";
import { annualOkrUrls } from "../../_redux/AnnualOkrUrls";

// ─────────────────────────────────────────────────────────────────────────────
// AnnualOkrReviewStore — the coach's async review pass (§6.2–6.7).
//
// Its own store rather than more state on `AnnualOkrStore`, matching
// `ExecutionBlueprintReviewStore`: the dashboard's data is a portfolio list
// that outlives any one client, and the review's is one run's worth that the
// coach opens, works through and leaves.
//
// Vanilla zustand + a hand-rolled selector hook, per CLAUDE.md. Define
// selectors at module scope, never inline — the hook resubscribes on
// `selector` identity change.
//
// Cached by `uniqueId` (`reviewByUniqueId`), the convention CLAUDE.md sets:
// coaches switch between clients constantly, and this is what makes coming back
// to one instant. `{ force }` bypasses.
// ─────────────────────────────────────────────────────────────────────────────

const nodeInstance = axios.create({
  baseURL: process.env.REACT_APP_NEST_BASE_URL,
});

const fill = (template, values) =>
  Object.entries(values).reduce(
    (url, [key, value]) => url.replace(`{${key}}`, encodeURIComponent(value)),
    template,
  );

const useAnnualOkrReviewStore = createStore((set, get) => ({
  // §6.3 payloads, keyed by run.
  reviewByUniqueId: {},
  // One flag per action, per CLAUDE.md — a status PUT must not blank the page.
  reviewLoading: false,
  enterLoading: false,
  statusPendingId: null,
  commentPending: false,
  submitLoading: false,
  startingLiveReview: false,

  // Copied verbatim from `AnnualOkrStore._resolveAuthHeader`, which is itself a
  // verbatim copy of `ExecutionBlueprintStore`'s — CLAUDE.md says copy this
  // rather than reinvent it, and the same 401/403 note applies.
  _resolveAuthHeader: () => {
    const accessToken = getCookie(ACCESS_TOKEN_COOKIE_KEY);
    const adminAccessToken = getCookie(ADMIN_ACCESS_TOKEN_COOKIE_KEY);
    const adminsCoachId = getCookie(ADMINS_COACH_ID_COOKIE_KEY);
    if (accessToken) return `Bearer ${accessToken}`;
    if (adminAccessToken && adminsCoachId) return `Bearer ${adminAccessToken}`;
    return null;
  },

  _headers: () => {
    const authHeader = get()._resolveAuthHeader();
    return authHeader ? { Authorization: authHeader } : {};
  },

  // ── §6.2 POST /review/enter ───────────────────────────────────────────────
  // SUBMITTED_FOR_COACH → COACH_REVIEW_IN_PROGRESS, idempotent if already
  // there. Swallows its error on purpose: a 409 here means the run has moved
  // past review, and the GET below will say so far more usefully than a toast
  // fired before the screen has even painted.
  enterReview: async (uniqueId) => {
    if (!uniqueId) return null;
    set({ enterLoading: true });
    try {
      const response = await nodeInstance.post(
        fill(annualOkrUrls.POST_REVIEW_ENTER, { uniqueId }),
        {},
        { headers: get()._headers() },
      );
      return response.data?.data || null;
    } catch {
      return null;
    } finally {
      set({ enterLoading: false });
    }
  },

  // ── §6.3 GET /review ──────────────────────────────────────────────────────
  fetchReview: async (uniqueId, { force = false } = {}) => {
    if (!uniqueId) return null;
    if (!force && get().reviewByUniqueId[uniqueId]) {
      return get().reviewByUniqueId[uniqueId];
    }
    set({ reviewLoading: true });
    try {
      const response = await nodeInstance.get(
        fill(annualOkrUrls.GET_REVIEW, { uniqueId }),
        { headers: get()._headers() },
      );
      const data = response.data?.data || null;
      set((state) => ({
        reviewByUniqueId: { ...state.reviewByUniqueId, [uniqueId]: data },
        reviewLoading: false,
      }));
      return data;
    } catch (error) {
      errorNoti(
        error.response?.data?.message || "Could not load the Annual OKR review",
      );
      set({ reviewLoading: false });
      return null;
    }
  },

  // ── §6.4 PUT …/objectives/:id/status ──────────────────────────────────────
  // Optimistic, and **rolls back to the PREVIOUS value rather than clearing**
  // — the rule CLAUDE.md sets out and `setBlockStatus` demonstrates. Clearing
  // on failure would turn a reviewed Objective into an unreviewed one and
  // silently move the submit gate.
  //
  // The endpoint returns only the row that changed, so the aggregate counts are
  // recomputed client-side (`recomputeCounts`) rather than refetched.
  setObjectiveStatus: async (uniqueId, annualObjectiveId, status) => {
    const before = get().reviewByUniqueId[uniqueId];
    if (!before) return null;

    const previous =
      (before.objectives || []).find(
        (objective) =>
          String(objective.annual_objective_id) === String(annualObjectiveId),
      )?.review_status ?? null;

    const apply = (value) =>
      set((state) => {
        const current = state.reviewByUniqueId[uniqueId];
        if (!current) return {};
        const objectives = (current.objectives || []).map((objective) =>
          String(objective.annual_objective_id) === String(annualObjectiveId)
            ? { ...objective, review_status: value }
            : objective,
        );
        return {
          reviewByUniqueId: {
            ...state.reviewByUniqueId,
            [uniqueId]: recomputeCounts({ ...current, objectives }),
          },
        };
      });

    apply(status);
    set({ statusPendingId: annualObjectiveId });

    try {
      const response = await nodeInstance.put(
        fill(annualOkrUrls.PUT_OBJECTIVE_STATUS, {
          uniqueId,
          annualObjectiveId,
        }),
        { status },
        { headers: get()._headers() },
      );
      // Reconcile with the server's own value rather than trusting the
      // optimistic one — same reason `setBlockStatus` does.
      const saved = response.data?.data?.status;
      if (saved && saved !== status) apply(saved);
      return response.data?.data || null;
    } catch (error) {
      apply(previous);
      errorNoti(
        error.response?.data?.message || "Could not save the review status",
      );
      return null;
    } finally {
      set({ statusPendingId: null });
    }
  },

  // ── §6.5 POST /review/comments ────────────────────────────────────────────
  // NOT optimistic: the server mints `coach_comment_id` and `created_at`, and
  // a comment without an id cannot be deleted — so a placeholder row would be
  // one the coach could see and not remove. Waits for the real row.
  addComment: async (uniqueId, { scopeType, scopeKey = null, text }) => {
    const body = {
      scope_type: scopeType,
      comment_text: text,
      // §6.5: required when scope_type is "objective", and it is the Objective
      // id as a STRING.
      ...(scopeType === "objective" ? { scope_key: String(scopeKey) } : {}),
    };
    set({ commentPending: true });
    try {
      const response = await nodeInstance.post(
        fill(annualOkrUrls.POST_COMMENT, { uniqueId }),
        body,
        { headers: get()._headers() },
      );
      const comment = response.data?.data;
      if (comment) {
        set((state) => {
          const current = state.reviewByUniqueId[uniqueId];
          if (!current) return {};
          return {
            reviewByUniqueId: {
              ...state.reviewByUniqueId,
              [uniqueId]: recomputeCounts(
                insertComment(current, scopeType, scopeKey, comment),
              ),
            },
          };
        });
      }
      return comment || null;
    } catch (error) {
      errorNoti(error.response?.data?.message || "Could not add the comment");
      throw error;
    } finally {
      set({ commentPending: false });
    }
  },

  // ── §6.5 DELETE /review/comments/:id ──────────────────────────────────────
  // Own comments only — a 403 here means someone else wrote it, which is worth
  // saying plainly rather than failing silently.
  deleteComment: async (uniqueId, commentId) => {
    try {
      await nodeInstance.delete(
        fill(annualOkrUrls.DELETE_COMMENT, { uniqueId, commentId }),
        { headers: get()._headers() },
      );
      set((state) => {
        const current = state.reviewByUniqueId[uniqueId];
        if (!current) return {};
        return {
          reviewByUniqueId: {
            ...state.reviewByUniqueId,
            [uniqueId]: recomputeCounts(removeComment(current, commentId)),
          },
        };
      });
      return true;
    } catch (error) {
      errorNoti(
        error.response?.data?.message ||
          "Could not delete the comment — you can only delete your own.",
      );
      return false;
    }
  },

  // ── §6.7 POST /review/submit ──────────────────────────────────────────────
  // Re-throws: the caller needs the 409 to tell "objectives still unreviewed"
  // (whose message carries the count) from a 400 on a bad date/time pair, and
  // the modal stays open either way.
  submitReview: async (uniqueId, payload) => {
    set({ submitLoading: true });
    try {
      const response = await nodeInstance.post(
        fill(annualOkrUrls.POST_REVIEW_SUBMIT, { uniqueId }),
        payload,
        { headers: get()._headers() },
      );
      // The run's state moved, so the cached review is stale; refetched rather
      // than patched because `annual_okr_state` gates the whole screen.
      await get().fetchReview(uniqueId, { force: true });
      return response.data?.data || null;
    } catch (error) {
      throw error;
    } finally {
      set({ submitLoading: false });
    }
  },
  // ── §6.8 POST /live-review/start ──────────────────────────────────────────
  // COACH_REVIEW_COMPLETED or LIVE_REVIEW_SCHEDULED → LIVE_REVIEW_IN_PROGRESS.
  // Either is a valid entry point, so the coach can start early rather than
  // waiting for a slot they scheduled.
  //
  // Returns `{ ok }` rather than throwing, matching EXW's own
  // `startLiveReview`: the caller's only decision is whether to navigate, and
  // the error is already on screen by then.
  startLiveReview: async (uniqueId) => {
    set({ startingLiveReview: true });
    try {
      const response = await nodeInstance.post(
        fill(annualOkrUrls.POST_LIVE_REVIEW_START, { uniqueId }),
        {},
        { headers: get()._headers() },
      );
      return { ok: true, data: response.data?.data || null };
    } catch (error) {
      errorNoti(
        error.response?.data?.message || "Could not start the live review",
      );
      return { ok: false };
    } finally {
      set({ startingLiveReview: false });
    }
  },
}));

// ─── Aggregate recomputation ────────────────────────────────────────────────
// Every mutation endpoint above returns only the row that changed (§6.4, §6.5),
// so the header's counts are derived here rather than refetched. CLAUDE.md
// names this as the house pattern.
//
// `reviewed_count` counts a non-null `review_status`, which is exactly the
// submit gate §6.7 describes — so the screen can disable Send with the same
// number the server would 409 on.
export const recomputeCounts = (review) => {
  const objectives = review.objectives || [];
  const commentCount =
    (review.overall_comments || []).length +
    objectives.reduce(
      (total, objective) => total + (objective.comments || []).length,
      0,
    );
  return {
    ...review,
    total_objectives: objectives.length,
    reviewed_count: objectives.filter((o) => o.review_status != null).length,
    accepted_count: objectives.filter((o) => o.review_status === "accepted")
      .length,
    to_discuss_count: objectives.filter((o) => o.review_status === "to_discuss")
      .length,
    comment_count: commentCount,
  };
};

const insertComment = (review, scopeType, scopeKey, comment) => {
  if (scopeType === "overall") {
    return {
      ...review,
      overall_comments: [...(review.overall_comments || []), comment],
    };
  }
  return {
    ...review,
    objectives: (review.objectives || []).map((objective) =>
      String(objective.annual_objective_id) === String(scopeKey)
        ? { ...objective, comments: [...(objective.comments || []), comment] }
        : objective,
    ),
  };
};

// The id alone is enough — a comment lives in exactly one place, and searching
// both lists is cheaper than threading its scope through the delete call.
const removeComment = (review, commentId) => ({
  ...review,
  overall_comments: (review.overall_comments || []).filter(
    (comment) => String(comment.coach_comment_id) !== String(commentId),
  ),
  objectives: (review.objectives || []).map((objective) => ({
    ...objective,
    comments: (objective.comments || []).filter(
      (comment) => String(comment.coach_comment_id) !== String(commentId),
    ),
  })),
});

export const useAnnualOkrReviewStoreSelector = (selector) => {
  const [selected, setSelected] = useState(() =>
    selector(useAnnualOkrReviewStore.getState()),
  );
  useEffect(() => {
    const unsub = useAnnualOkrReviewStore.subscribe((state) => {
      setSelected(() => selector(state));
    });
    return () => unsub();
  }, [selector]);
  return selected;
};

export default useAnnualOkrReviewStore;
export { useAnnualOkrReviewStore };
